// The public signup page (M3-22), at /signup for anyone while the Cell serves it: a business owner gives the
// business's name, their own name and email, and consents to the privacy notice; LAMP makes the business and emails
// a code. The code, entered here on the same device, sets the business up and signs them in, and onboarding opens.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError } from "../http.js";
import { otherLanguage, useLanguage, useWords } from "../i18n/words.js";
import { noticeHref } from "../legal/Notice.js";
import type { SignupApi } from "./api.js";

export const SIGNUP_PATH = "/signup";
/** Where a new Owner lands once signed in: onboarding. */
export const ONBOARDING_PATH = "/set-up";

export function Signup({ api, onSignedUp }: { api: SignupApi; onSignedUp: () => void }) {
  const w = useWords();
  const { language, setLanguage } = useLanguage();
  const [businessName, setBusinessName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const codeInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (tenantId) codeInput.current?.focus();
  }, [tenantId]);

  const signUp = async (event: FormEvent) => {
    event.preventDefault();
    if (!consent) return;
    setBusy(true);
    setProblem(undefined);
    try {
      const made = await api.signUp({
        businessName: businessName.trim(),
        name: name.trim(),
        email: email.trim(),
        consent,
      });
      setTenantId(made.tenantId);
    } catch (error) {
      setProblem(error instanceof ApiError && error.status === 429 ? w.signupTooMany : w.signupCheck);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    setProblem(undefined);
    setNote(undefined);
    try {
      await api.verify(tenantId, email.trim(), code.trim(), trust);
      onSignedUp();
    } catch (error) {
      setProblem(error instanceof ApiError && error.status >= 500 ? w.signupFailed : w.signupBadCode);
      codeInput.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!tenantId) return;
    setProblem(undefined);
    try {
      await api.resend(tenantId, email.trim());
      setNote(w.signupResent);
    } catch (error) {
      setProblem(error instanceof ApiError && error.status === 429 ? w.signupTooMany : w.signupFailed);
    }
  };

  return (
    <main className="sign-in public-page" id="main">
      <p className="sign-in-language">
        <button type="button" className="chip" onClick={() => setLanguage(otherLanguage(language))}>
          {w.languageSwitch}
        </button>
      </p>
      <h1>{w.signupTitle}</h1>
      {tenantId === undefined ? (
        <>
          <p>{w.signupIntro}</p>
          <form onSubmit={signUp}>
            <label className="field">
              {w.signupBusiness}
              <input
                value={businessName}
                required
                maxLength={200}
                autoComplete="organization"
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </label>
            <label className="field">
              {w.signupName}
              <input
                value={name}
                required
                maxLength={120}
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="field">
              {w.signupEmail}
              <input
                type="email"
                value={email}
                required
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="check">
              <input type="checkbox" checked={consent} required onChange={(e) => setConsent(e.target.checked)} />
              {w.signupConsent}
            </label>
            <p>
              <a href={noticeHref("privacy-notice", language)}>{w.legalPrivacyNotice}</a>
              {" · "}
              <a href="/trust">{w.signupPrivacyLink}</a>
            </p>
            <button type="submit" className="btn btn-go" disabled={busy || !consent}>
              {w.signupSubmit}
            </button>
          </form>
          <p>
            {w.signupNotServed} <a href="/waitlist">{w.signupWaitlistLink}</a>
          </p>
          <p>
            {w.signupHaveAccount} <a href="/">{w.signupSignInLink}</a>
          </p>
        </>
      ) : (
        <form onSubmit={verify}>
          <p>{w.signupSent(email.trim())}</p>
          <label className="field">
            {w.signupCode}
            <input
              ref={codeInput}
              value={code}
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <label className="check">
            <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
            {w.signupTrust}
          </label>
          <button type="submit" className="btn btn-go" disabled={busy}>
            {w.signupVerify}
          </button>
          <button type="button" className="linkish" onClick={() => void resend()}>
            {w.signupResend}
          </button>
        </form>
      )}
      {note ? <p role="status">{note}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </main>
  );
}
